<template>
  <main class="research-page">
    <header class="research-hero">
      <div class="research-container">
        <NuxtLink class="research-back" :to="localePath('/research')">← Workspace369 Research</NuxtLink>
        <div class="research-hero-grid">
          <div>
            <h1>{{ report.title }}</h1>
            <p class="research-lede">{{ report.description }}</p>
            <div class="research-byline">
              <NuxtLink :to="localePath('/about')">Research and calculations by Workspace369</NuxtLink>
              <span>Published <time :datetime="report.datePublished">{{ date(report.datePublished) }}</time></span>
              <span>Updated <time :datetime="report.dateModified">{{ date(report.dateModified) }}</time></span>
            </div>
          </div>
          <aside class="research-scope" aria-label="Dataset scope">
            <dl>
              <div><dt>Measured population</dt><dd>{{ report.scope }}</dd></div>
              <div><dt>Observation period</dt><dd>{{ report.period }}</dd></div>
              <div><dt>Sources checked</dt><dd><template v-if="sourceCheckRange"><time :datetime="sourceCheckRange.start">{{ date(sourceCheckRange.start) }}</time><template v-if="sourceCheckRange.end !== sourceCheckRange.start"> – <time :datetime="sourceCheckRange.end">{{ date(sourceCheckRange.end) }}</time></template></template></dd></div>
            </dl>
            <a href="#methodology">Read the methodology ↗</a>
          </aside>
        </div>
        <div class="research-actions">
          <a class="research-button research-button-primary" :href="download('csv')" @click="trackResearch('download_click', report.slug, 'csv')" download>Download data · CSV <span aria-hidden="true">↓</span></a>
          <a class="research-button" :href="download('json')" @click="trackResearch('download_click', report.slug, 'json')" download>Source ledger · JSON <span aria-hidden="true">↓</span></a>
          <a class="research-text-link" href="#cite">Cite this report ↗</a>
        </div>
      </div>
    </header>

    <div class="research-container research-layout">
      <nav class="research-outline" aria-label="On this page">
        <p class="research-eyebrow">In this report</p>
        <a v-for="item in leadSections" :key="item.id" :href="`#${item.id}`">{{ item.title }}</a>
        <a href="#takeaways">Key takeaways</a>
        <a v-for="item in bodySections" :key="item.id" :href="`#${item.id}`">{{ item.title }}</a>
        <a v-if="report.calculator" href="#calculator">Try the calculator</a>
        <a href="#methodology">Methodology & limitations</a>
        <a href="#sources">Primary sources</a>
        <a href="#cite">Citation & downloads</a>
      </nav>
      <article class="research-article">
        <template v-for="part in orderedSections" :key="part.id">
        <section v-if="part.id === takeawaysId" id="takeaways" class="research-takeaways research-section" aria-labelledby="takeaway-heading">
          <div class="research-section-top"><span>2026 edition</span></div>
          <h2 id="takeaway-heading">Key takeaways</h2>
          <p>Each takeaway links to its canonical data row. Reported figures belong to their cited producer; modeled results are labeled as calculations.</p>
          <ol>
            <li v-for="id in report.highlights" :key="id">
              <a :href="`#${id}`">{{ byId[id].claim }}</a>
              <small>{{ byId[id].kind === 'calculated' ? 'Workspace369 calculation' : sourceById[byId[id].sourceId].publisher }}</small>
            </li>
          </ol>
        </section>

        <!-- The report's first trial button, right after its key findings: the existing in-article CTA with the full
             trial terms under the button (legal B1.1). It hides the phone trial bar while it is on screen. -->
        <InlineCta v-if="part.id === takeawaysId" label-prefix="Research report" />

        <figure v-if="illustration && part.id === takeawaysId" class="research-illustration">
          <img :src="illustration.src" :srcset="illustration.srcset"
            sizes="(max-width: 760px) calc(100vw - 36px), (max-width: 1000px) calc(100vw - 264px), (max-width: 1304px) calc(100vw - 332px), 972px"
            :alt="illustration.alt" :width="illustration.width" :height="illustration.height"
            loading="lazy" decoding="async" />
          <figcaption>{{ illustration.caption || 'AI-generated editorial illustration; not a data visualization.' }}</figcaption>
        </figure>

        <section v-if="part.id !== takeawaysId" :id="part.id" class="research-section">
          <h2>{{ part.title }}</h2>
          <p class="research-answer">{{ part.answer }}</p>
          <p v-if="part.columns.length > 2" class="research-note research-table-hint">Scroll horizontally to compare all columns →</p>
          <div v-if="part.rows.length" class="research-table-scroll" tabindex="0" role="region" :aria-label="`${part.title} Data table; scroll horizontally if needed.`">
            <table>
              <caption>{{ report.shortTitle }} — {{ part.id === 'trend' ? 'historical series shown by year' : report.period }}. {{ report.geography }}.</caption>
              <thead><tr><th v-for="(col, i) in part.columns" :key="i" scope="col">{{ col }}</th></tr></thead>
              <tbody>
                <tr v-for="(row, i) in part.rows" :key="i">
                  <th scope="row">{{ row[0] }}</th>
                  <td v-for="(cell, j) in row.slice(1)" :id="cell.stat || undefined" :key="j" :class="{ 'research-stat': cell.stat, 'research-text': part.textCells && !cell.stat, 'research-missing': (!cell.stat && !part.textCells) || byId[cell.stat]?.value === null }">
                    <template v-if="cell.stat">
                      <a v-if="byId[cell.stat].kind === 'reported'" :href="sourceById[byId[cell.stat].sourceId].url" :title="`${byId[cell.stat].claim} Source: ${sourceById[byId[cell.stat].sourceId].publisher}. ${byId[cell.stat].locator}`" target="_blank" rel="noopener noreferrer"><template v-if="splitValue(byId[cell.stat])"><span translate="no">{{ splitValue(byId[cell.stat])[0] }}</span> <span>{{ splitValue(byId[cell.stat])[1] }}</span></template><template v-else>{{ formatValue(byId[cell.stat]) }}</template><span class="research-source-mark" aria-hidden="true">↗</span></a>
                      <a v-else href="#methodology" :title="`${byId[cell.stat].claim} Calculation: ${byId[cell.stat].formula}`"><template v-if="splitValue(byId[cell.stat])"><span translate="no">{{ splitValue(byId[cell.stat])[0] }}</span> <span>{{ splitValue(byId[cell.stat])[1] }}</span></template><template v-else>{{ formatValue(byId[cell.stat]) }}</template><span class="research-source-mark" aria-hidden="true">*</span></a>
                      <small v-if="byId[cell.stat].evidenceType" class="research-evidence-type">{{ byId[cell.stat].evidenceType }}</small>
                    </template>
                    <template v-else>{{ cell }}</template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-if="part.note" class="research-note">{{ part.note }}</p>
          <p class="research-source-line" v-if="part.sourceIds.length">Primary {{ part.sourceIds.length === 1 ? 'source' : 'sources' }}:
            <template v-for="(id, i) in part.sourceIds" :key="id"><span v-if="i"> · </span><a :href="`#source-${id}`">{{ sourceById[id].publisher }} — {{ sourceById[id].publicationDate ? date(sourceById[id].publicationDate) : `checked ${date(sourceById[id].accessed)}` }}</a></template>
          </p>
        </section>
        </template>

        <PaymentFeeCalculator v-if="report.calculator === 'payments'" data-sticky-cta-hide />
        <ProjectPricingCalculator v-else-if="report.calculator === 'project-pricing'" data-sticky-cta-hide />
        <ResearchCalculator v-else-if="report.calculator" :type="report.calculator" data-sticky-cta-hide />

        <section id="methodology" class="research-section research-methodology">
          <h2>Methodology and limitations</h2>
          <ol><li v-for="text in report.methodology" :key="text">{{ text }}</li></ol>
          <h3>What these numbers cannot tell you</h3>
          <ul><li v-for="text in report.limitations" :key="text">{{ text }}</li></ul>
          <h3>Freshness and corrections</h3>
          <p>Maintained by the {{ report.owner }}. {{ report.cadence }} The edition date changes only when the evidence or content is substantively reviewed; it does not change the underlying observation period.</p>
          <p>First edition: <time :datetime="report.datePublished">{{ date(report.datePublished) }}</time>. Data extraction, source attribution and arithmetic checked for this edition. No independent peer review is claimed.</p>
          <p>Found an error or a newer primary release? <a :href="correctionUrl">Send a correction with the source and affected statistic</a>. Confirmed corrections are recorded below before republishing.</p>
          <h3>Revision history</h3>
          <p>Evidence dates above describe the report. Download and citation updates below do not imply that sources were checked again.</p>
          <ul class="research-revisions"><li v-for="entry in release.revisions" :key="entry.date + entry.summary"><time :datetime="entry.date">{{ date(entry.date) }}</time> — {{ entry.summary }}</li></ul>
        </section>

        <section id="sources" class="research-section">
          <h2>Primary sources and provenance</h2>
          <p>Every reported numeric cell links directly to its producer. The downloads include exact table or workbook locators, observation periods, access dates, formulas and input references.</p>
          <ol class="research-sources">
            <li v-for="item in report.sources" :id="`source-${item.id}`" :key="item.id">
              <strong>{{ item.publisher }}</strong>
              <a :href="item.url" target="_blank" rel="noopener noreferrer">{{ item.title }} ↗</a>
              <small>{{ item.publicationDate ? `Published ${date(item.publicationDate)}.` : 'Publication date not recorded; see the original source.' }} Accessed {{ date(item.accessed) }}.</small>
              <small v-if="item.originalUrl">Archived copy: this link opens the Internet Archive snapshot we read. Original address: <span translate="no">{{ item.originalUrl }}</span></small>
              <small>{{ item.license }}</small>
              <details v-if="item.sha256"><summary>Source file fingerprint (SHA-256)</summary><code class="research-hash">{{ item.sha256 }}</code></details>
            </li>
          </ol>
        </section>

        <!-- data-sticky-cta-hide here and on the calculators: the phone trial bar steps aside while their own buttons are on screen. -->
        <section id="cite" class="research-section research-citation" data-sticky-cta-hide>
          <span id="dataset" aria-hidden="true"></span>
          <h2>How to cite this report</h2>
          <p>For a source-reported statistic, credit the original publisher and link to the exact row here when using our compilation. For a modeled result, cite Workspace369 and include the assumptions. Linking to this page does not make us the original producer of third-party data.</p>
          <blockquote>{{ citation }}</blockquote>
          <button class="research-button" type="button" @click="copyCitation"><span v-show="!copied">Copy citation</span><span v-show="copied">Citation copied</span></button>
          <span v-show="copyError" class="research-copy-status" role="status">Clipboard unavailable. Select and copy the citation text above.</span>
          <div class="research-downloads">
            <a :href="download('csv')" @click="trackResearch('download_click', report.slug, 'csv')" download>Download the citation-ready data (CSV) ↓</a>
            <a :href="download('json')" @click="trackResearch('download_click', report.slug, 'json')" download>Download the complete source ledger (JSON) ↓</a>
          </div>
          <div class="research-downloads">
            <a :href="download('bib')" download>Reference manager · BibTeX ↓</a>
            <a :href="download('ris')" download>Reference manager · RIS ↓</a>
          </div>
          <p v-if="report.chart"><a :href="download('chart.svg')" download>Download reusable chart with sources and limitations ↓</a></p>
          <div v-if="embedHtml" class="research-embed">
            <h3>Embed this chart</h3>
            <p>Paste this HTML into your article to show the chart with a credit link back to this report and its sources.</p>
            <textarea class="research-embed-code" readonly rows="5" translate="no" aria-label="Chart embed code" :value="embedHtml" @focus="$event.target.select()" />
            <button class="research-button" type="button" @click="copyEmbed"><span v-show="!embedCopied">Copy embed code</span><span v-show="embedCopied">Embed code copied</span></button>
            <span v-show="embedError" class="research-copy-status" role="status">Clipboard unavailable. Select and copy the code above.</span>
          </div>
          <h3>Versioned data</h3>
          <p>Use an archived file when your analysis needs to stay reproducible. The version identifies the exact CSV and JSON bytes; unchanged data keeps the same version.</p>
          <p>Dataset version: <code class="research-hash" translate="no">{{ release.dataset_version }}</code></p>
          <div class="research-downloads">
            <a v-for="file in release.files.filter(file => ['csv', 'json'].includes(file.format))" :key="file.format" :href="file.url" download><span>Archived data</span> · <span translate="no">{{ file.format.toUpperCase() }}</span> ↓</a>
            <a :href="release.manifest_url" download>Download checksums and version manifest ↓</a>
          </div>
          <p class="research-note">Reference exports describe Workspace369’s compilation. When citing a reported observation, also credit its original producer.</p>
          <p class="research-note"><a :href="researchLicenseUrl" hreflang="en">The downloads are English-language reference datasets, including on translated pages. Source rights remain with their producers. Attribute Workspace369’s compilation and calculations; consult each source’s reuse terms.</a></p>
        </section>

        <section class="research-section research-related">
          <h2>Continue the research</h2>
          <NuxtLink v-for="item in otherReports" :key="item.slug" :to="localePath(`/research/${item.slug}`)">{{ item.shortTitle }} <span aria-hidden="true">→</span></NuxtLink>
          <h3>From the data to day-to-day operations</h3>
          <p v-if="moneySentenceParts" class="research-note research-money-sentence">{{ moneySentenceParts.sentence }} <NuxtLink :to="localePath(moneyLink.path)">{{ moneySentenceParts.text }} <span aria-hidden="true">→</span></NuxtLink></p>
          <NuxtLink v-for="item in relatedLinks" :key="item.path" :to="localePath(item.path)">{{ item.label }} <span aria-hidden="true">→</span></NuxtLink>
        </section>
      </article>
    </div>
    <PhoneStickyCta label="Research report" :location="$route.path" watch=".research-hero" />
  </main>
</template>

<script setup>
import { getSourceCheckRange } from '~/data/research/provenance.mjs';
import PaymentFeeCalculator from './PaymentFeeCalculator.vue';
import ProjectPricingCalculator from './ProjectPricingCalculator.vue';
import { formatValue, reports, researchOrigin } from '~/data/research/reports.mjs';
import { researchLicenseUrl } from '~/data/research/license.mjs';
import { researchMoneyLinks, pricingLink, moneySentence } from '~/data/money-links.mjs';
import releases from '~/data/research/release-manifest.json';
import illustrations from '~/data/research/illustrations.json';
const trackResearch = useResearchTracking();
const props = defineProps({ report: { type: Object, required: true } });
const sourceCheckRange = computed(() => getSourceCheckRange(props.report.sources));
const release = computed(() => releases[props.report.slug]);
const illustration = computed(() => illustrations[props.report.slug]);
const { localePath } = useWorkspaceLocale();
// Numbers stay in English data formatting; only the unit word after them is translated.
const splitUnits = { days: 'days', 'million people': 'million', 'USD billion': 'billion', 'USD trillion': 'trillion' };
const splitValue = (stat) => {
  const word = splitUnits[stat.unit];
  if (!word || stat.value === null) return null;
  return [formatValue(stat).slice(0, -(word.length + 1)), word];
};
// Sections marked `lead` answer the page's head query, so they render above the takeaways.
const takeawaysId = 'takeaways';
const leadSections = computed(() => props.report.sections.filter((s) => s.lead));
const bodySections = computed(() => props.report.sections.filter((s) => !s.lead));
const orderedSections = computed(() => [...leadSections.value, { id: takeawaysId }, ...bodySections.value]);
const byId = computed(() => Object.fromEntries(props.report.stats.map((s) => [s.id, s])));
const sourceById = computed(() => Object.fromEntries(props.report.sources.map((s) => [s.id, s])));
const otherReports = computed(() => reports.filter((r) => r.slug !== props.report.slug));
const moneyLink = computed(() => researchMoneyLinks[props.report.slug] || null);
const moneySentenceParts = computed(() => moneySentence(moneyLink.value));
// Each report links on to the matching solution page and pricing (data/money-links.mjs).
const relatedLinks = computed(() => {
  const extra = [researchMoneyLinks[props.report.slug], pricingLink].filter(Boolean);
  const seen = new Set((props.report.related || []).map((item) => item.path));
  return [...(props.report.related || []), ...extra.filter((item) => !seen.has(item.path))];
});
const date = (value) => new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T12:00:00Z`));
const download = (extension) => `/downloads/research/${props.report.slug}.${extension}`;
const citation = computed(() => `Workspace369. (${props.report.dateModified}). ${props.report.title}. ${researchOrigin}/research/${props.report.slug}/. Primary sources and observation periods as listed in the report.`);
const correctionUrl = computed(() => `mailto:support@workspace369.com?subject=${encodeURIComponent(`Research correction: ${props.report.title}`)}`);
const copied = ref(false);
const copyError = ref(false);
// Embed code only for reports with a source-data chart image (not editorial illustrations).
const escapeAttr = (value) => String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const embedHtml = computed(() => {
  const image = illustration.value;
  if (!props.report.chart || !image || !/-chart-\d+\.webp$/.test(image.src)) return '';
  const url = `${researchOrigin}/research/${props.report.slug}/`;
  const title = escapeAttr(props.report.title);
  return `<figure style="margin:0;max-width:${image.width}px">`
    + `<a href="${url}"><img src="${researchOrigin}${image.src}" alt="${escapeAttr(image.alt)}" width="${image.width}" height="${image.height}" loading="lazy" style="width:100%;height:auto"></a>`
    + `<figcaption>Source: <a href="${url}">${title}</a>, Workspace369 (original data producers are credited in the report).</figcaption></figure>`;
});
const embedCopied = ref(false);
const embedError = ref(false);
async function copyEmbed() {
  embedCopied.value = false;
  embedError.value = false;
  try { await navigator.clipboard.writeText(embedHtml.value); embedCopied.value = true; embedError.value = false; trackResearch('embed_copy', props.report.slug); }
  catch { embedCopied.value = false; embedError.value = true; }
}
async function copyCitation() {
  copied.value = false;
  copyError.value = false;
  try { await navigator.clipboard.writeText(citation.value); copied.value = true; copyError.value = false; trackResearch('citation_copy', props.report.slug); }
  catch { copied.value = false; copyError.value = true; }
}
</script>
